import { useState } from 'react'
import { NavLink, Link } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import GameSwitcher from './GameSwitcher'
import LanguageSwitcher from './LanguageSwitcher'
import { useLanguage } from '@/localization'

const links = [
  { to: '/', label: 'Home', end: true },
  { to: '/servers', label: 'Servers' },
  { to: '/mods', label: 'Mods' },
  { to: '/join', label: 'Join' },
  { to: '/rules', label: 'Rules' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/#news', label: 'News' },
  { to: '/#contact', label: 'Contact' },
]

export default function Navigation() {
  const [open, setOpen] = useState(false)
  const { t } = useLanguage()

  const baseClass = 'px-3 py-2 rounded-lg text-sm font-bold transition-colors'
  const inactiveClass = 'text-white/80 hover:text-white hover:bg-white/10'
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `${baseClass} ${isActive ? 'bg-grass-dark text-white' : inactiveClass}`
  // Section anchors (/#news, /#contact) resolve to path "/", so a NavLink would
  // mark them route-active on every home page. Render them as plain anchors with
  // the inactive style instead, so only the real current route highlights.
  const anchorClass = `${baseClass} ${inactiveClass}`

  return (
    <header className="sticky top-0 z-40 bg-slate-deep/95 backdrop-blur border-b-4 border-grass-dark shadow-block">
      <nav className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 shrink-0">
          <GameSwitcher currentId="minecraft" />
          <Link to="/" className="flex flex-col leading-none" onClick={() => setOpen(false)}>
            <span className="font-pixel text-[11px] text-grass">ULIUNAI</span>
            <span className="text-white/70 text-xs font-bold tracking-widest">MINECRAFT</span>
          </Link>
        </div>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) =>
            l.to.startsWith('/#') ? (
              <a key={l.to} href={l.to} className={anchorClass}>
                {t(l.label)}
              </a>
            ) : (
              <NavLink key={l.to} to={l.to} end={l.end} className={linkClass}>
                {t(l.label)}
              </NavLink>
            ),
          )}
          <LanguageSwitcher />
        </div>

        <button
          className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 cursor-pointer"
          onClick={() => setOpen((v) => !v)}
          aria-label={t('Toggle menu')}
          aria-expanded={open}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-slate-deep px-4 py-3 grid grid-cols-2 gap-2">
          {links.map((l) =>
            l.to.startsWith('/#') ? (
              <a key={l.to} href={l.to} className={anchorClass} onClick={() => setOpen(false)}>
                {t(l.label)}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={linkClass}
                onClick={() => setOpen(false)}
              >
                {t(l.label)}
              </NavLink>
            ),
          )}
          <LanguageSwitcher compact />
        </div>
      )}
    </header>
  )
}
